
import { useState, useEffect, createContext, useContext, type ReactNode } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';

// Only create Supabase client if credentials are provided
const supabase = supabaseUrl && supabaseKey 
  ? createClient(supabaseUrl, supabaseKey)
  : null;

// Debug flag for development
const DEBUG_CART = true; // Enable for debugging

export interface CartItem {
  id: string;
  name: string;
  price: number;
  image: string;
  size: string;
  color: string;
  quantity: number;
  category: string;
  subcategory: string;
}

export interface DeliveryInfo {
  location: string;
  fee: number;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
}

export interface PromoCode {
  code: string;
  discount: number;
  type: 'percentage' | 'fixed';
}

// Create context
interface CartContextType {
  cartItems: CartItem[];
  deliveryInfo: DeliveryInfo | null;
  appliedPromo: PromoCode | null;
  isSubmittingOrder: boolean;
  addToCart: (product: any, size: string, color: string, quantity?: number) => boolean;
  removeFromCart: (id: string, size: string, color: string) => void;
  updateQuantity: (id: string, size: string, color: string, quantity: number) => void;
  clearCart: () => void;
  getCartItemsCount: () => number;
  getCartTotal: () => number;
  getDiscountAmount: () => number;
  getFinalTotal: () => number;
  applyPromoCode: (promo: PromoCode) => void;
  removePromoCode: () => void;
  updateDeliveryInfo: (info: DeliveryInfo) => void;
  generateWhatsAppMessage: () => string;
  submitOrderToSupabase: () => Promise<any>;
  processOrderAndWhatsApp: () => Promise<any>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

// Provider component
export function CartProvider({ children }: { children: ReactNode }) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo | null>(null);
  const [appliedPromo, setAppliedPromo] = useState<PromoCode | null>(null);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('shamiz-cart');
      const savedDelivery = localStorage.getItem('shamiz-delivery');
      const savedPromo = localStorage.getItem('shamiz-promo');
      
      if (DEBUG_CART) console.log('🔄 Loading cart from localStorage:', savedCart);
      
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        if (Array.isArray(parsedCart)) {
          if (DEBUG_CART) console.log('✅ Loaded cart items:', parsedCart);
          setCartItems(parsedCart);
        }
      }
      if (savedDelivery) {
        const parsedDelivery = JSON.parse(savedDelivery);
        setDeliveryInfo(parsedDelivery);
      }
      if (savedPromo) {
        const parsedPromo = JSON.parse(savedPromo);
        setAppliedPromo(parsedPromo);
      }
    } catch (error) {
      console.error('❌ Error loading cart from localStorage:', error);
      // Clear corrupted data
      localStorage.removeItem('shamiz-cart');
      localStorage.removeItem('shamiz-delivery');
      localStorage.removeItem('shamiz-promo');
    }
  }, []);

  // Save cart to localStorage whenever it changes - SINGLE SOURCE OF TRUTH
  useEffect(() => {
    try {
      if (DEBUG_CART) console.log('💾 Saving cart to localStorage:', cartItems);
      localStorage.setItem('shamiz-cart', JSON.stringify(cartItems));
    } catch (error) {
      console.error('❌ Error saving cart to localStorage:', error);
    }
  }, [cartItems]);

  useEffect(() => {
    try {
      if (deliveryInfo) {
        localStorage.setItem('shamiz-delivery', JSON.stringify(deliveryInfo));
      } else {
        localStorage.removeItem('shamiz-delivery');
      }
    } catch (error) {
      console.error('Error saving delivery info:', error);
    }
  }, [deliveryInfo]);

  useEffect(() => {
    try {
      if (appliedPromo) {
        localStorage.setItem('shamiz-promo', JSON.stringify(appliedPromo));
      } else {
        localStorage.removeItem('shamiz-promo');
      }
    } catch (error) {
      console.error('Error saving promo code:', error);
    }
  }, [appliedPromo]);

  const addToCart = (product: any, size: string, color: string, quantity: number = 1) => {
    try {
      if (DEBUG_CART) {
        console.log('🛒 === ADD TO CART START ===');
        console.log('📦 Product:', product);
        console.log('📏 Size:', size);
        console.log('🎨 Color:', color);
        console.log('🔢 Quantity:', quantity);
        console.log('🆔 Product ID:', product?.id);
        console.log('📋 Product Name:', product?.name);
      }
      
      // Enhanced validation with detailed logging
      if (!product) {
        console.error('❌ Product is null or undefined');
        return false;
      }
      
      if (!product.id) {
        console.error('❌ Product ID is missing:', product);
        return false;
      }
      
      if (!size || size.trim() === '') {
        console.error('❌ Size is missing or empty:', size);
        return false;
      }
      
      if (!color || color.trim() === '') {
        console.error('❌ Color is missing or empty:', color);
        return false;
      }
      
      if (!quantity || quantity <= 0) {
        console.error('❌ Invalid quantity:', quantity);
        return false;
      }

      // Normalize ID to string to prevent type mismatches
      const normalizedId = String(product.id);
      
      if (DEBUG_CART) {
        console.log('🔄 Normalized ID:', normalizedId);
        console.log('📊 Current cart before add:', cartItems);
      }

      // Use functional state update to prevent race conditions
      setCartItems(prevItems => {
        if (DEBUG_CART) {
          console.log('🔍 Previous cart items:', prevItems);
          console.log('🔍 Looking for existing item with:', { id: normalizedId, size, color });
        }
        
        const existingItemIndex = prevItems.findIndex(
          item => {
            const match = item.id === normalizedId && item.size === size && item.color === color;
            if (DEBUG_CART) {
              console.log(`🔍 Checking item ${item.id} (${item.size}, ${item.color}): ${match ? '✅ MATCH' : '❌ NO MATCH'}`);
            }
            return match;
          }
        );

        let updatedItems: CartItem[];

        if (existingItemIndex > -1) {
          // Update existing item quantity
          updatedItems = [...prevItems];
          const oldQuantity = updatedItems[existingItemIndex].quantity;
          updatedItems[existingItemIndex].quantity += quantity;
          if (DEBUG_CART) {
            console.log(`🔄 Updated existing item quantity: ${oldQuantity} → ${updatedItems[existingItemIndex].quantity}`);
            console.log('📦 Updated item:', updatedItems[existingItemIndex]);
          }
        } else {
          // Add new item to cart
          const newItem: CartItem = {
            id: normalizedId,
            name: product.name,
            price: product.price,
            image: product.image,
            size,
            color,
            quantity,
            category: product.category || 'general',
            subcategory: product.subcategory || 'general'
          };
          updatedItems = [...prevItems, newItem];
          if (DEBUG_CART) {
            console.log('➕ Added new item to cart:', newItem);
          }
        }

        if (DEBUG_CART) {
          console.log('📊 Final updated cart items:', updatedItems);
          console.log('📈 Cart items count:', updatedItems.length);
          console.log('🛒 === ADD TO CART SUCCESS ===');
        }
        
        return updatedItems;
      });

      return true;
    } catch (error: any) {
      console.error('❌ Error adding item to cart:', error);
      console.error('❌ Error details:', {
        productId: product?.id,
        productName: product?.name,
        size,
        color,
        quantity,
        error: error?.message
      });
      return false;
    }
  };

  const removeFromCart = (id: string, size: string, color: string) => {
    try {
      const normalizedId = String(id);
      if (DEBUG_CART) {
        console.log('🗑️ Removing from cart:', { id: normalizedId, size, color });
      }
      
      setCartItems(prevItems => {
        const filteredItems = prevItems.filter(item => 
          !(item.id === normalizedId && item.size === size && item.color === color)
        );
        
        if (DEBUG_CART) {
          console.log('📊 Cart after removal:', filteredItems);
        }
        
        return filteredItems;
      });
    } catch (error) {
      console.error('❌ Error removing item from cart:', error);
    }
  };

  const updateQuantity = (id: string, size: string, color: string, quantity: number) => {
    try {
      if (quantity <= 0) {
        removeFromCart(id, size, color);
        return;
      }

      const normalizedId = String(id);
      if (DEBUG_CART) {
        console.log('🔄 Updating quantity:', { id: normalizedId, size, color, quantity });
      }
      
      setCartItems(prevItems => 
        prevItems.map(item => 
          item.id === normalizedId && item.size === size && item.color === color
            ? { ...item, quantity }
            : item
        )
      );
    } catch (error) {
      console.error('❌ Error updating quantity:', error);
    }
  };

  const clearCart = () => {
    try {
      if (DEBUG_CART) {
        console.log('🧹 Clearing cart');
      }
      
      setCartItems([]);
      setDeliveryInfo(null);
      setAppliedPromo(null);
      localStorage.removeItem('shamiz-cart');
      localStorage.removeItem('shamiz-delivery');
      localStorage.removeItem('shamiz-promo');
    } catch (error) {
      console.error('❌ Error clearing cart:', error);
    }
  };

  const getCartItemsCount = () => {
    const count = cartItems.reduce((total, item) => total + item.quantity, 0);
    if (DEBUG_CART) {
      console.log('📊 Cart items count:', count);
    }
    return count;
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getDiscountAmount = () => {
    if (!appliedPromo) return 0;
    
    const subtotal = getCartTotal();
    if (appliedPromo.type === 'percentage') {
      return Math.round(subtotal * (appliedPromo.discount / 100));
    } else {
      return Math.min(appliedPromo.discount, subtotal);
    }
  };

  const getFinalTotal = () => {
    const subtotal = getCartTotal();
    const discount = getDiscountAmount();
    const deliveryFee = deliveryInfo?.fee || 0;
    return subtotal - discount + deliveryFee;
  };

  const applyPromoCode = (promo: PromoCode) => {
    setAppliedPromo(promo);
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
  };

  const updateDeliveryInfo = (info: DeliveryInfo) => {
    setDeliveryInfo(info);
  };

  const generateOrderNotes = () => {
    let notes = `Order contains ${cartItems.length} item(s): `;
    notes += cartItems.map(item => `${item.name} (${item.size}, ${item.color}) x${item.quantity}`).join(', ');
    
    if (appliedPromo) {
      notes += `. Promo code "${appliedPromo.code}" applied for ${appliedPromo.type === 'percentage' ? appliedPromo.discount + '%' : 'KSh ' + appliedPromo.discount} discount.`;
    }
    
    return notes;
  };

  const submitOrderToSupabase = async () => {
    if (cartItems.length === 0 || !deliveryInfo) {
      throw new Error('Cart is empty or delivery info missing');
    }

    setIsSubmittingOrder(true);

    try {
      const orderData = {
        customer_name: deliveryInfo.customerName,
        customer_phone: deliveryInfo.customerPhone,
        customer_address: deliveryInfo.customerAddress,
        delivery_location: deliveryInfo.location,
        delivery_fee: deliveryInfo.fee,
        items: cartItems,
        subtotal: getCartTotal(),
        discount_amount: getDiscountAmount(),
        promo_code: appliedPromo?.code || null,
        total_amount: getFinalTotal(),
        status: 'pending',
        order_date: new Date().toISOString(),
        notes: generateOrderNotes()
      };

      // Only save to Supabase if configured
      let data = null;
      if (supabase) {
        const { data: dbData, error } = await supabase
          .from('orders')
          .insert([orderData])
          .select()
          .single();

        if (error) {
          console.error('Supabase error:', error);
          // Don't throw error, just log it and continue with WhatsApp
        } else {
          data = dbData;
        }
      }

      // Clear cart after successful submission
      clearCart();
      
      return data;
    } catch (error) {
      console.error('Order submission error:', error);
      throw error;
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const generateWhatsAppMessage = () => {
    if (cartItems.length === 0) return '';

    let message = `🛍️ *Shamiz Designer — New Order Alert*\n\n`;
    
    message += `A new customer order has just been placed through the website/WhatsApp system. Please review the details below:\n\n`;
    
    if (deliveryInfo?.customerName) {
      message += `*Customer Information:*\n`;
      message += `👤 Name: ${deliveryInfo.customerName}\n`;
      message += `📞 Phone: ${deliveryInfo.customerPhone}\n`;
      message += `📍 Location: ${deliveryInfo.location}\n`;
      if (deliveryInfo.customerAddress) {
        message += `🏠 Address: ${deliveryInfo.customerAddress}\n`;
      }
      message += `\n`;
    }

    message += `*Order Details:*\n\n`;
    cartItems.forEach((item) => {
      message += `*Product:* ${item.name}\n`;
      message += `*Category:* ${item.category} - ${item.subcategory}\n`;
      message += `*Size:* ${item.size}\n`;
      message += `*Color:* ${item.color}\n`;
      message += `*Quantity:* ${item.quantity} × KSh ${item.price.toLocaleString()} = KSh ${(item.price * item.quantity).toLocaleString()}\n\n`;
    });

    const subtotal = getCartTotal();
    message += `*Order Summary:*\n\n`;
    message += `*Subtotal:* KSh ${subtotal.toLocaleString()}\n`;

    if (appliedPromo) {
      const discount = getDiscountAmount();
      message += `*Discount (${appliedPromo.code}):* -KSh ${discount.toLocaleString()}\n`;
    }

    if (deliveryInfo) {
      message += `*Delivery Fee (${deliveryInfo.location}):* KSh ${deliveryInfo.fee.toLocaleString()}\n`;
    }

    const finalTotal = getFinalTotal();
    message += `\n*Total:* KSh ${finalTotal.toLocaleString()}\n\n`;
    
    message += `⚙️ *Action Required:*\n`;
    message += `Please confirm this order with the customer and proceed with packaging and delivery arrangements.\n\n`;
    
    message += `💼 *System Message:*\n`;
    message += `This notification was automatically generated by Shamiz Designer's online order system.`;

    return encodeURIComponent(message);
  };

  const processOrderAndWhatsApp = async () => {
    try {
      // First save to Supabase
      const savedOrder = await submitOrderToSupabase();
      
      // Then open WhatsApp with order details
      const whatsappMessage = generateWhatsAppMessage();
      const whatsappUrl = `https://wa.me/254720307375?text=${whatsappMessage}`;
      window.open(whatsappUrl, '_blank');
      
      return savedOrder;
    } catch (error) {
      console.error('Order processing error:', error);
      throw error;
    }
  };

  // Debug logging for cart state
  if (DEBUG_CART) {
    console.log('🛒 Current cart state:', {
      itemsCount: cartItems.length,
      items: cartItems,
      total: getCartTotal()
    });
  }

  const value = {
    cartItems,
    deliveryInfo,
    appliedPromo,
    isSubmittingOrder,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartItemsCount,
    getCartTotal,
    getDiscountAmount,
    getFinalTotal,
    applyPromoCode,
    removePromoCode,
    updateDeliveryInfo,
    generateWhatsAppMessage,
    submitOrderToSupabase,
    processOrderAndWhatsApp
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// Hook to use cart context
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
